/* global GM_STATE */
import { updateMultipleCartItems } from 'libs/cart'
import { emitterProps, globalEmitter } from 'helpers/emitter-event'
import { selectAll, removeClass, addClass } from 'helpers/dom'

/**
 * Logout Gift Validator
 * Detects when user logs out and removes first-time subscriber gifts
 * that require logged-in status to verify subscription history.
 *
 * Note: Product-specific gifts do NOT require login and are NOT removed.
 *
 * Runs on every page load to catch logout events regardless of which page
 * the user lands on after clicking logout.
 */

const STORAGE_KEY = 'nutra_customer_logged'

/**
 * Check if user logged out and remove ineligible gifts
 */
async function validateGiftsOnLogout() {
  try {
    // Get previous and current login states
    const prevLoginState = sessionStorage.getItem(STORAGE_KEY)
    const currentLoginState = GM_STATE.customer.logged ? 'true' : 'false'

    // Update storage with current state for next page load
    sessionStorage.setItem(STORAGE_KEY, currentLoginState)

    // Only proceed if user just logged out (was logged in, now logged out)
    if (prevLoginState === 'true' && currentLoginState === 'false') {
      // Fetch current cart to check for product-specific gifts
      const cartResponse = await fetch('/cart.js')

      if (!cartResponse.ok) return

      const cart = await cartResponse.json()

      if (!cart || !cart.items || cart.items.length === 0) return

      // Find first-time subscriber gifts that require logged-in status
      const subscriberGiftsToRemove = cart.items.filter(item => {
        return item.properties?._isSubscriberGift === true
      })

      // Remove first-time subscriber gifts if found
      if (subscriberGiftsToRemove.length > 0) {
        // 1. Update GM_STATE.cart immediately for instant UI update
        // This ensures components that read from GM_STATE show correct data on mount
        if (GM_STATE.cart && GM_STATE.cart.initCart) {
          GM_STATE.cart.initCart.items = GM_STATE.cart.initCart.items.filter(item => {
            return item.properties?._isSubscriberGift !== true
          })

          // Recalculate cart item count
          GM_STATE.cart.initCart.item_count = GM_STATE.cart.initCart.items.reduce(
            (sum, item) => sum + item.quantity,
            0
          )
        }

        // 2. Remove via Shopify API (persists to backend)
        const updates = {}

        subscriberGiftsToRemove.forEach(gift => {
          updates[gift.key] = 0 // Set quantity to 0 to remove
        })

        const updatedCart = await updateMultipleCartItems({ updates })

        // 3. Update GM_STATE with fresh cart data from API response
        if (updatedCart && GM_STATE.cart) {
          GM_STATE.cart.initCart = updatedCart
        }

        // 4. Update cart count in header directly and emit event
        if (updatedCart) {
          // Update all cart count elements (uses id="BenefitTabs" in header)
          const cartCountElements = selectAll('#BenefitTabs', document.body)

          if (cartCountElements) {
            const count = updatedCart.item_count

            if (count === 0) {
              cartCountElements.forEach((el) => addClass('hidden', el))
            } else {
              cartCountElements.forEach((el) => {
                el.innerText = count
                removeClass('hidden', el)
                addClass('flex', el)
              })
            }
          }

          globalEmitter.emit(emitterProps.cart.change, updatedCart.item_count)
        }
      }
    }
  } catch (error) {
    // Silently fail - don't block page load or show errors
    // Gift will be removed when user visits cart page via existing validation
    console.warn('Logout gift validation failed:', error)
  }
}

// Auto-execute on module load (runs on every page)
validateGiftsOnLogout()

export default validateGiftsOnLogout
